import { useState, useId, ChangeEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as EyeIcon } from "../../../assets/eye.icon.svg";
import { ReactComponent as EyeOffIcon } from "../../../assets/eye-off.icon.svg";
import { AuthErrorMessage } from "../../../common/enums/auth-error-message.enum";
import { FirebaseErrorCode } from "../../../common/enums/firebase-error-code.enum";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { useTenantAuthOptions } from "../../../common/utilities/tenant-auth/use-tenant-auth-options.util";
import { passwordMeetsRules } from "../../../common/utilities/password-policy/password-policy.util";
import { AuthTemplate } from "../../../components/auth-template/auth-template.comp";
import { PasswordRules } from "../../../components/auth/password-rules/password-rules.comp";
import { Loader } from "../../../components/loader/loader.comp";

type Field = "current" | "next" | "confirm";

/**
 * Set a new password (tenant_authentication_plan.md Phase 3). Reached from
 * the API interceptor with ?reason=expired when the backend refuses a
 * password that is past the tenant's expiry window; also usable directly.
 * Re-authenticates with the current password, so it needs no live Firebase
 * SDK session (tenant subdomains often have none after an auth-domain login).
 */
const ChangePassword = () => {
    const { email, changePassword, logout } = FirebaseAuthContainer.useContainer();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const expired = searchParams.get("reason") === "expired";
    const authOptions = useTenantAuthOptions(window.location.origin);
    const passwordPolicy = authOptions?.passwordPolicy ?? null;

    const [login, setLogin] = useState<string>(email ?? "");
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [visible, setVisible] = useState<Record<Field, boolean>>({
        current: false,
        next: false,
        confirm: false
    });
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const errorId = useId();
    const rulesId = useId();

    const onChange = (setter: (v: string) => void) => (evt: ChangeEvent<HTMLInputElement>) => {
        setter(evt.target.value ?? "");
        setErrorMessage("");
    };
    const toggle = (field: Field) => () => setVisible(v => ({ ...v, [field]: !v[field] }));

    const handleSubmit = async () => {
        const userEmail = (email || login).trim();
        if (!userEmail || !current || !next || !confirm || isLoading) return;
        if (!passwordMeetsRules(passwordPolicy, next)) {
            setErrorMessage(AuthErrorMessage.passwordPolicy);
            return;
        }
        if (next !== confirm) {
            setErrorMessage(AuthErrorMessage.differentPasswords);
            return;
        }
        if (next === current) {
            setErrorMessage(AuthErrorMessage.samePassword);
            return;
        }
        setIsLoading(true);
        try {
            await changePassword(userEmail, current, next);
            navigate("/", { replace: true });
        } catch (error: any) {
            switch (error?.code as FirebaseErrorCode) {
                case FirebaseErrorCode.wrongPassword:
                case FirebaseErrorCode.invalidCredential:
                    setErrorMessage(AuthErrorMessage.invalidCredential);
                    break;
                case FirebaseErrorCode.passwordPolicy:
                case FirebaseErrorCode.weakPassword:
                    setErrorMessage(AuthErrorMessage.passwordPolicy);
                    break;
                case FirebaseErrorCode.manyRequests:
                case FirebaseErrorCode.tooManyAttempts:
                    setErrorMessage(AuthErrorMessage.tooManyAttempts);
                    break;
                default:
                    setErrorMessage(AuthErrorMessage.default);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const renderPassword = (
        field: Field,
        label: string,
        value: string,
        setter: (v: string) => void,
        autoComplete: string,
        describedBy?: string
    ) => (
        <div className="change-password-field">
            <input
                className="change-password-field__input"
                type={visible[field] ? "text" : "password"}
                aria-label={label}
                autoComplete={autoComplete}
                required
                aria-invalid={errorMessage ? true : undefined}
                aria-describedby={
                    [errorMessage ? errorId : "", describedBy ?? ""].filter(Boolean).join(" ") ||
                    undefined
                }
                placeholder={label}
                onChange={onChange(setter)}
                value={value}
            />
            <button
                type="button"
                className="change-password-field__eye"
                aria-label={visible[field] ? "Hide password" : "Show password"}
                aria-pressed={visible[field]}
                onClick={toggle(field)}
            >
                <SvgIcon
                    component={visible[field] ? EyeIcon : EyeOffIcon}
                    inheritViewBox
                    aria-hidden="true"
                />
            </button>
        </div>
    );

    const canSubmit = !!(email || login) && !!current && !!next && !!confirm && !isLoading;

    return (
        <AuthTemplate>
            <div className="change-password">
                <div className="change-password-title" role="heading" aria-level={1}>
                    {expired ? "Your password has expired" : "Change password"}
                </div>
                <div className="change-password-description">
                    {expired
                        ? "This organization requires a new password at regular intervals. Set one to continue."
                        : "Enter your current password and choose a new one."}
                </div>
                <form
                    className="change-password-content"
                    onSubmit={evt => {
                        evt.preventDefault();
                        handleSubmit();
                    }}
                >
                    {!email && (
                        <div className="change-password-field">
                            <input
                                className="change-password-field__input"
                                type="email"
                                aria-label="Email"
                                autoComplete="username"
                                required
                                placeholder="Email"
                                onChange={onChange(setLogin)}
                                value={login}
                            />
                        </div>
                    )}
                    {renderPassword(
                        "current",
                        "Current password",
                        current,
                        setCurrent,
                        "current-password"
                    )}
                    {renderPassword(
                        "next",
                        "New password",
                        next,
                        setNext,
                        "new-password",
                        next ? rulesId : undefined
                    )}
                    {next && <PasswordRules policy={passwordPolicy} password={next} id={rulesId} />}
                    {renderPassword(
                        "confirm",
                        "Confirm new password",
                        confirm,
                        setConfirm,
                        "new-password"
                    )}
                    <div id={errorId} className="change-password-message" role="alert">
                        {errorMessage && <span>{errorMessage}</span>}
                    </div>
                    <div className="change-password-action">
                        <Button
                            className="primary-button"
                            variant="text"
                            type="submit"
                            disabled={!canSubmit}
                        >
                            {!isLoading ? (
                                <span>Set new password</span>
                            ) : (
                                <Loader size={24} borderWidth={4} />
                            )}
                        </Button>
                        <Button className="change-password-logout" variant="text" onClick={logout}>
                            Log out
                        </Button>
                    </div>
                </form>
            </div>
        </AuthTemplate>
    );
};

export default ChangePassword;
